import React, { useState, useEffect } from "react";
import {
  Button,
  Select,
  Form,
  Input,
  DatePicker,
  Row,
  Col,
  Transfer,
  Drawer,
  Typography,
  Space,
  Popover,
  notification,
} from "antd";
import axiosInstance from "utilities/axios";

const { Text } = Typography;

const AddRegionForm = ({ open, onClose, onChange }) => {
  const [form] = Form.useForm();

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [clinicOptions, setClinicOptions] = useState([]);

  const [tempSelected, setTempSelected] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedClinics, setSelectedClinics] = useState([]);
  const [targetKeys, setTargetKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [regionCode, setRegionCode] = useState(null);
  const [province, setProvince] = useState(null);

  useEffect(() => {
    axiosInstance.get("/employees").then((response) => {
      setEmployeeOptions(response.data);
    });

    axiosInstance.get("/regions/accessible").then((response) => {
      setRegionOptions(response.data);
    });

    axiosInstance.get("/divisions/accessible").then((response) => {
      setDivisionOptions(response.data);
    });
  }, []);

  const onSelectChange = (sourceSelectedKeys, targetSelectedKeys) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
  };

  const handleTransferChange = (nextTargetKeys, direction, moveKeys) => {
    setTargetKeys(nextTargetKeys);

    if (direction === "left") {
      setSelectedClinics([
        ...selectedClinics,
        ...clinicOptions.filter((clinic) => moveKeys.includes(clinic.clinicNo)),
      ]);
    }
  };

  const onFinish = (values) => {
    axiosInstance
      .post("/regions", {
        temporary: tempSelected,
        regionCode: values.code,
        regionName: values.name,
        divisionCode: values.division,
        employee: values.employee,
        startDate: values.startDate.format("YYYY-MM-DD"),
        endDate: values.endDate ? values.endDate.format("YYYY-MM-DD") : null,
        clinics: selectedClinics.map((clinic) => clinic.clinicNo),
      })
      .then(() => {
        notification.success({
          message: "Region Created",
          description: "The region has been successfully created.",
        });
        onChange();
        onClose();
      })
      .catch((error) => {
        notification.error({
          message: "Failed to create region",
          description: error.response.data.error,
        });
      });
  };

  const handleRegionSelect = (value) => {
    setSelectedRegion(value);

    axiosInstance
      .get(`/regions/${value}`)
      .then((response) => {
        const filteredClinics = response.data.clinics.filter(
          (clinic) =>
            !selectedClinics.some(
              (selectedClinic) => selectedClinic.clinicNo === clinic.clinicNo
            )
        );
        setClinicOptions([...selectedClinics, ...filteredClinics]);
        setTargetKeys(filteredClinics.map((clinic) => clinic.clinicNo));
      })
      .catch((err) => {
        notification.error({
          message: "Error Fetching Clinics",
          description: err.response.data.error,
        });
      });
  };

  const handleRegionCodeGenerate = () => {
    if (province === undefined || province === null) {
      notification.error({
        message: "Please select a province",
        description: "Please select a province to generate a region code",
      });
      return;
    }

    if (form.getFieldValue("type") === undefined || form.getFieldValue("type") === null) {
      notification.error({
        message: "Please select a region type",
        description: "Please select a region type before generating a region code",
      });
      return;
    }

    axiosInstance
      .get(`/regions/province/${province}`, {
        params: { temporary: tempSelected },
      })
      .then((response) => {
        setRegionCode(response.data.regionCode);
        form.setFieldsValue({ code: response.data.regionCode });
      })
      .catch((error) => {
        notification.error({
          message: "Failed to generate region code",
          description: error.response.data.error,
        });
      });
  };

  const popoverContent = (
    <>
      <Select
        style={{ width: "100%" }}
        placeholder="Select a province"
        onChange={(value) => setProvince(value)}
      >
        <Select.Option value="ON">Ontario</Select.Option>
        <Select.Option value="AB">Alberta</Select.Option>
        <Select.Option value="MB">Manitoba</Select.Option>
        <Select.Option value="NS">Nova Scotia</Select.Option>
        <Select.Option value="SK">Saskatchewan</Select.Option>
        <Select.Option value="BC">British Columbia</Select.Option>
        <Select.Option value="NB">New Brunswick</Select.Option>
      </Select>
      <Button
        type="primary"
        style={{ width: "100%", marginTop: 10 }}
        onClick={handleRegionCodeGenerate}
      >
        Generate
      </Button>
    </>
  );

  return (
    <Drawer
      title="Add Region"
      open={open}
      onClose={onClose}
      footer={[]}
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={(changedValues, allValues) => {
          if (changedValues.type) {
            setTempSelected(changedValues.type === "Temporary");
          }
        }}
      >
        <Form.Item
          name="type"
          label="Region Type"
          rules={[{ required: true, message: "Please select a region type" }]}
        >
          <Select
            placeholder="Select a region type"
            style={{ width: "100%" }}
            trigger="click"
          >
            <Select.Option value="Temporary">Temporary</Select.Option>
            <Select.Option value="Permanent">Permanent</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="code"
          label="Region Code"
          rules={[
            {
              required: true,
              message: "Please enter a region code",
            }
          ]}
        >
          <Space.Compact style={{ width: "100%" }}>
            <Input
              placeholder="Enter region code"
              value={regionCode}
              onChange={(e) => setRegionCode(e.target.value)}
              readOnly
            />
            <Popover
              placement="bottomRight"
              content={popoverContent}
              trigger="click"
              title="Select a province to generate a region code"
            >
              <Button type="primary">Generate</Button>
            </Popover>
          </Space.Compact>
        </Form.Item>
        <Form.Item
          name="name"
          label="Region Name"
          rules={[
            {
              required: true,
              message: "Please enter a region name",
            },
            {
              max: 40,
              message: "Region name must be a max of 40 characters long",
            },
          ]}
        >
          <Input placeholder="Enter region name" />
        </Form.Item>
        <Form.Item
          name="division"
          label="Division"
          rules={[{ required: true, message: "Please select a division" }]}
        >
          <Select placeholder="Select a division" style={{ width: "100%" }}>
            {divisionOptions &&
              divisionOptions.map((division) => (
                <Select.Option
                  key={division.divisionCode}
                  value={division.divisionCode}
                >
                  {division.divisionName} - {division.divisionCode}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="employee"
          label="Select Regional Manager"
          rules={[{ required: true, message: "Please select an employee" }]}
        >
          <Select
            showSearch
            placeholder="Select an employee"
            style={{ width: "100%" }}
            filterOption={(input, option) => {
              const optionData = employeeOptions.find(
                (opt) => opt.initials === option.value
              );
              if (!optionData) {
                return false;
              }
              return (
                optionData.firstName
                  .toLowerCase()
                  .includes(input.toLowerCase()) ||
                optionData.lastName
                  .toLowerCase()
                  .includes(input.toLowerCase()) ||
                optionData.initials.toLowerCase().includes(input.toLowerCase())
              );
            }}
          >
            {employeeOptions.map((employee) => (
              <Select.Option key={employee.initials} value={employee.initials}>
                {employee.firstName} {employee.lastName} - {employee.initials}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="startDate"
              label="Start Date"
              rules={[
                { required: true, message: "Please select a start date" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          {tempSelected && (
            <Col span={12}>
              <Form.Item
                name="endDate"
                label="End Date"
                rules={[
                  { required: true, message: "Please select a start date" },
                ]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Text>Assign Clinics</Text>
        <Select
          placeholder="Select a region"
          style={{ width: "100%", marginBottom: 24, marginTop: 8 }}
          value={selectedRegion}
          onChange={handleRegionSelect}
        >
          {regionOptions &&
            regionOptions.map((region) => (
              <Select.Option key={region.regionCode} value={region.regionCode}>
                {region.regionName} - {region.regionCode}
              </Select.Option>
            ))}
        </Select>
        <Transfer
          dataSource={clinicOptions}
          targetKeys={targetKeys}
          selectedKeys={selectedKeys}
          rowKey={(record) => record.clinicNo}
          onSelectChange={onSelectChange}
          onChange={handleTransferChange}
          on
          render={(item) => `${item.clinicNo} - ${item.clinicName}`}
          disabled={!selectedRegion}
          titles={[null, selectedRegion]}
          listStyle={{ width: "45%", height: 300 }}
          style={{ width: "100%", marginBottom: 24 }}
          locale={{ itemUnit: "Clinic", itemsUnit: "Clinics" }}
        />
        <Button type="primary" htmlType="submit" style={{ width: "100%" }}>
          Create New Region
        </Button>
      </Form>
    </Drawer>
  );
};

export default AddRegionForm;
